import { useEffect, useRef } from 'react';

type UseApprovalKeyboardShortcutsParams = {
  onApprove: () => void;
  onDeny: () => void;
  enabled?: boolean;
};

/**
 * Registers window-level keyboard shortcuts for tool approval actions.
 * Cmd/Ctrl+Enter → approve, Cmd/Ctrl+Backspace or Delete → deny.
 *
 * Callbacks are captured in refs so the listener is only re-registered when
 * `enabled` changes, not on every render.
 */
const useApprovalKeyboardShortcuts = ({
  onApprove,
  onDeny,
  enabled = true,
}: UseApprovalKeyboardShortcutsParams) => {
  const onApproveRef = useRef(onApprove);
  onApproveRef.current = onApprove;
  const onDenyRef = useRef(onDeny);
  onDenyRef.current = onDeny;

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;

      if (isCmdOrCtrl && e.key === 'Enter') {
        e.preventDefault();
        onApproveRef.current();
      } else if (isCmdOrCtrl && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault();
        onDenyRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
};

export default useApprovalKeyboardShortcuts;
